export interface AgentStreamEvent {
  type: 'log' | 'error' | 'result' | 'canvas_checkpoint' | 'application_settlement_requested' | 'visual_review_capture_requested' | 'visual_review_status' | 'turn_lifecycle' | string;
  data?: any;
}

export interface TurnLifecycleEvent {
  request_id: string;
  turn_id: string;
}

export function parseTurnLifecycleEvent(data: unknown): TurnLifecycleEvent | null {
  if (!data || typeof data !== 'object') return null;
  const candidate = data as Record<string, unknown>;
  const requestId = candidate.request_id;
  const turnId = candidate.turn_id;
  if (typeof requestId !== 'string' || requestId.trim().length === 0) return null;
  if (typeof turnId !== 'string' || turnId.trim().length === 0) return null;
  return { request_id: requestId, turn_id: turnId };
}

/** Round 11 B1/B2 lifecycle correction. Carries the SAME
 * ChangeApplicationEnvelope shape the terminal `result` event's
 * `metadata.change_application` already uses — this is the pre-terminal
 * delivery of that exact envelope, sent early so the frontend can apply it
 * and start capturing BEFORE the turn's terminal completion arrives. The
 * terminal event's own `change_application` is set to
 * `{already_delivered: true, ...}` for any turn that fired this event, so
 * the terminal-apply path (streamingAgentRunner.ts) must never re-apply. */
export interface VisualReviewCaptureRequestedEvent {
  turn_id: string;
  capture_token: string;
  change_application: unknown;
}

/** Content-free handshake for one authorized application. Rendering is a
 * separate optional capability and therefore has no capture token here. */
export interface ApplicationSettlementRequestedEvent {
  turn_id: string;
  settlement_token: string;
  change_application: unknown;
}

export function parseApplicationSettlementRequestedEvent(
  data: unknown,
): ApplicationSettlementRequestedEvent | null {
  if (!data || typeof data !== 'object') return null;
  const candidate = data as Record<string, unknown>;
  if (typeof candidate.turn_id !== 'string' || !candidate.turn_id.trim()) return null;
  if (typeof candidate.settlement_token !== 'string' || !candidate.settlement_token.trim()) return null;
  return {
    turn_id: candidate.turn_id,
    settlement_token: candidate.settlement_token,
    change_application: candidate.change_application ?? null,
  };
}

export function parseVisualReviewCaptureRequestedEvent(
  data: unknown,
): VisualReviewCaptureRequestedEvent | null {
  if (!data || typeof data !== 'object') return null;
  const candidate = data as Record<string, unknown>;
  const turnId = candidate.turn_id;
  const captureToken = candidate.capture_token;
  if (typeof turnId !== 'string' || turnId.trim().length === 0) return null;
  if (typeof captureToken !== 'string' || captureToken.trim().length === 0) return null;
  return {
    turn_id: turnId,
    capture_token: captureToken,
    change_application: candidate.change_application ?? null,
  };
}

/** Round 11 B1/B2 lifecycle correction. Status transitions for the
 * "Reviewing rendered result…" UI state — `status: 'reviewing'` shown
 * while the original request is held open awaiting the capture ack and
 * review dispatch; `status: 'resolved'` (carrying the final
 * render_observed/reason_code) marks the point just before the terminal
 * `result` event arrives. */
export interface VisualReviewStatusEvent {
  status: 'reviewing' | 'resolved' | string;
  render_observed?: boolean;
  reason_code?: string | null;
}

export function parseVisualReviewStatusEvent(data: unknown): VisualReviewStatusEvent | null {
  if (!data || typeof data !== 'object') return null;
  const candidate = data as Record<string, unknown>;
  const status = candidate.status;
  if (typeof status !== 'string' || status.trim().length === 0) return null;
  return {
    status,
    render_observed: typeof candidate.render_observed === 'boolean' ? candidate.render_observed : undefined,
    reason_code: typeof candidate.reason_code === 'string' ? candidate.reason_code : null,
  };
}

/** Round 11 final-correction Task B — SHARED-FINAL-REVIEW-001. The honest
 * status label the unified "Final review" UI control shows,
 * derived ONLY from the same render_observed/reason_code/authorization
 * truth the backend already produces — never a fourth, invented state, and
 * never phrased as if pixels were reviewed when they were not.
 *
 * Product-language correction (Round 11 final correction): the prior
 * wording exposed "Structural review" vs "Rendered review" as if they were
 * two separate controls/results. There is exactly ONE user-facing control
 * ("Final review · On / Off") and exactly one status line per turn:
 *
 * - No change was applied this turn (authorization !== "apply" -- an
 *   approval pause, a withheld/failed turn, a no-op turn): "Final review
 *   skipped — no change was applied". Takes priority over every other
 *   state, since Final review can never meaningfully run without an
 *   applied change regardless of whether it was requested.
 * - A change was applied, Final review was requested, and render_observed
 *   === true: "Final review complete" (a usable rendered-pixel review
 *   genuinely happened).
 * - A change was applied, Final review was requested, and render_observed
 *   === false (any reason code -- not eligible, no ack, capture
 *   unavailable, call failed): "Final review unavailable — rendered
 *   capture could not be obtained" — never silently omitted, since the
 *   user explicitly asked for it.
 * - A change was applied and the control exposed review capacity, but the
 *   agent did not request it: "Final review not requested — agent completed
 *   without it". Exposure is not a deterministic trigger; the agent owns
 *   the judgment about whether current rendered evidence is needed.
 * - A change was applied but the Final review CONTROL was off: "Final review
 *   did not run — the Final review control is off". Round 11 reliability
 *   settlement (workstream G): this state previously showed nothing at all,
 *   which is how the live incident (conversation b86677c4) misled the user.
 *   They asked for Final Review in their message, the durable control was
 *   off, deterministic `visual_quality_check` tool calls ran and were
 *   rendered as if they were the review, and no status line ever said the
 *   rendered review had not run. Silence read as success.
 *
 *   Prompt prose must not silently switch a disabled product control on —
 *   so the honest resolution is to say plainly that it did not run, and why.
 *   Deterministic/tool quality checks are a different process and never
 *   produce any of these labels. */
export type FinalReviewStatusLabel =
  | 'Final review skipped — the update wasn’t confirmed in time'
  | 'Final review skipped — a newer request has started'
  | 'Final review complete'
  | 'Final review unavailable — the reviewer could not finish'
  | 'Final review skipped — the review budget was exhausted'
  | 'Final review cancelled'
  | 'Final review unavailable — rendered capture could not be obtained'
  | 'Final review skipped — the agent completed without reviewing'
  | 'Final review skipped — not everything was built yet'
  | 'Final review skipped — no change was applied'
  | 'Final review did not run — the Final review control is off';

export function finalReviewStatusLabel(params: {
  changeApplied: boolean;
  reviewControlEnabled: boolean;
  visualReviewRequested: boolean;
  renderObserved?: boolean;
  reasonCode?: string | null;
  /** The turn ended with requested items still unbuilt. */
  workIncomplete?: boolean;
}): FinalReviewStatusLabel | null {
  const {
    changeApplied,
    reviewControlEnabled,
    visualReviewRequested,
    renderObserved,
  } = params;
  if (reviewControlEnabled && params.reasonCode === 'application_unconfirmed') return 'Final review skipped — the update wasn’t confirmed in time';
  if (reviewControlEnabled && params.reasonCode === 'newer_user_turn') return 'Final review skipped — a newer request has started';
  if (reviewControlEnabled && ['deadline_exhausted','turn_deadline_exhausted'].includes(params.reasonCode || '')) return 'Final review skipped — the review budget was exhausted';
  if (reviewControlEnabled && params.reasonCode === 'cancelled') return 'Final review cancelled';
  if (reviewControlEnabled && ['review_unavailable','review_execution_failed'].includes(params.reasonCode || '')) return 'Final review unavailable — the reviewer could not finish';
  if (!changeApplied) {
    return reviewControlEnabled ? 'Final review skipped — no change was applied' : null;
  }
  if (!reviewControlEnabled) {
    // A change WAS applied and a rendered review could genuinely have run,
    // but the control is off. Say so rather than showing nothing: an absent
    // status line is indistinguishable from a review that quietly passed.
    return 'Final review did not run — the Final review control is off';
  }
  if (!visualReviewRequested && params.workIncomplete) {
    // The review waits for the finished work; "completed without reviewing"
    // would claim the turn completed when it stopped part-way.
    return 'Final review skipped — not everything was built yet';
  }
  if (!visualReviewRequested) {
    // The control exposes review capacity; it does not compel a tool call.
    // The agent may truthfully finish without vision for an exact text edit
    // or another change it judges does not require rendered evidence.
    return 'Final review skipped — the agent completed without reviewing';
  }
  if (renderObserved === undefined) {
    // Requested but not yet resolved this turn -- nothing truthful to show.
    return null;
  }
  return renderObserved
    ? 'Final review complete'
    : params.reasonCode === 'capture_unavailable' ? 'Final review unavailable — rendered capture could not be obtained' : 'Final review unavailable — the reviewer could not finish';
}

/** Round 6 Part B — one settled-page checkpoint batch, as emitted by the
 * backend's canvas_checkpoint SSE event (`data` field of that event).
 *
 * `corrective`: Round 6 Part B close-out. false (or absent, pre-close-out
 * backend) means an ADDITIVE initial checkpoint — apply via the existing
 * command-normalization/execution route. true means this batch is a full
 * replacement of the target scene's design content — apply via
 * `VisualSurfaceAdapter.applyCanvasCheckpoint` with `corrective: true`, never
 * the plain additive path (see visualStudioAgentFlow.ts / commandExecutor.ts
 * `applyCorrectiveSceneReplacement`). */
export interface CanvasCheckpointEvent {
  batch_id: string;
  scene_id: string;
  item_id?: string | null;
  commands: unknown[];
  corrective: boolean;
}

/** Minimal shape validation — non-empty batch_id/scene_id, commands is an
 * array. Does not inspect command contents (that's the surface adapter's
 * job). Returns the narrowed event or null when malformed. `corrective`
 * normalizes to false when absent (backward compatibility with any
 * already-in-flight pre-close-out backend event), and to false for any
 * non-boolean value rather than rejecting the whole event. */
export function parseCanvasCheckpointEvent(data: unknown): CanvasCheckpointEvent | null {
  if (!data || typeof data !== 'object') return null;
  const candidate = data as Record<string, unknown>;
  const batchId = candidate.batch_id;
  const sceneId = candidate.scene_id;
  const commands = candidate.commands;
  if (typeof batchId !== 'string' || batchId.trim().length === 0) return null;
  if (typeof sceneId !== 'string' || sceneId.trim().length === 0) return null;
  if (!Array.isArray(commands)) return null;
  const itemId = typeof candidate.item_id === 'string' ? candidate.item_id : null;
  const corrective = candidate.corrective === true;
  return {
    batch_id: batchId.trim(),
    scene_id: sceneId.trim(),
    item_id: itemId?.trim() || null,
    commands,
    corrective,
  };
}

export interface StreamCloseDiagnostic {
  closeTime: string;
  errorType: 'abort' | 'network' | 'parse';
  visibilityState: string;
  lastEventType: string | null;
  lastSequence: number;
  requestId: string | null;
}

export async function* readAgentStreamEvents(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  options: {requestId?:string;onClose?:(diagnostic:StreamCloseDiagnostic)=>void} = {},
): AsyncGenerator<AgentStreamEvent> {
  const decoder = new TextDecoder();
  let buffer = '';
  let terminal = false;
  let errorType: StreamCloseDiagnostic['errorType'] = 'abort';
  let lastEventType: string | null = null;
  let lastSequence = 0;
  let requestId = options.requestId || null;
  const parse = (chunk:string) => {
    const line=chunk.split('\n').find(part=>part.startsWith('data: '));
    if (!line) return null;
    try {
      const event=JSON.parse(line.slice(6));
      lastEventType=event.type;
      lastSequence=Number.isInteger(event.sequence) ? event.sequence : lastSequence+1;
      if(event.type==='turn_lifecycle') requestId=event.data?.request_id || requestId;
      if(event.type==='result') terminal=true;
      return event as AgentStreamEvent;
    } catch(error) {errorType='parse';throw error;}
  };
  try {
    while (true) {
      let next: ReadableStreamReadResult<Uint8Array>;
      try {next=await reader.read();}
      catch(error) {errorType=(error as Error)?.name==='AbortError'?'abort':'network';throw error;}
      if(next.done) {
        buffer+=decoder.decode();
        if(buffer.trim()) {const event=parse(buffer);if(event) yield event;}
        errorType='network';break;
      }
      buffer+=decoder.decode(next.value,{stream:true});
      const chunks=buffer.split('\n\n');buffer=chunks.pop() || '';
      for(const chunk of chunks) {const event=parse(chunk);if(event) yield event;}
    }
  } finally {
    if(!terminal) {
      const diagnostic:StreamCloseDiagnostic={closeTime:new Date().toISOString(),errorType,visibilityState:typeof document==='undefined'?'unknown':document.visibilityState,lastEventType,lastSequence,requestId};
      // Existing browser debug channel only; no content or credentials.
      console.warn('[agent-stream-close]',diagnostic);
      options.onClose?.(diagnostic);
    }
  }
}
